import { AxiosRequestConfig } from "axios";
import { CohesiveAPIClient } from "../../client";
import { route } from "../../utils";
import {
  GetSubscriptionResponse,
  ListBillingPlansResponse,
  ListCreditUsagePerUserResponse,
  ListPaymentMethodsResponse,
  ListStripeInvoicesResponse,
  PreviewStripeInvoicePayload,
  PreviewStripeInvoiceResponse,
  SetupIntentResponse,
  UpdateSubscriptionPayload,
  UpdateSubscriptionResponse,
} from "./types";

export * from "./types";

const ENDPOINTS = {
  SETUP_INTENT: "/api/v2/org/:organization_id/billing/stripe-setup-intent",
  LIST_PAYMENT_METHODS:
    "/api/v2/org/:organization_id/billing/stripe-payment-methods",
  DELETE_PAYMENT_METHOD:
    "/api/v2/org/:organization_id/billing/stripe-payment-methods/:payment_method_id",
  LIST_BILLING_PLANS: "/api/v2/billing-plans",
  UPDATE_SUBSCRIPTION:
    "/api/v2/org/:organization_id/billing/stripe-subscription",
  GET_SUBSCRIPTION: "/api/v2/org/:organization_id/billing/stripe-subscription",
  LIST_STRIPE_INVOICES: "/api/v2/org/:organization_id/billing/stripe-invoices",
  PREVIEW_STRIPE_INVOICE:
    "/api/v2/org/:organization_id/billing/stripe-invoice-preview",
  LIST_CREDIT_USAGE_PER_USER:
    "/api/v2/org/:organization_id/billing/credit-usage-per-user",
};

export class BillingAPI {
  constructor(private client: CohesiveAPIClient) {}

  async createSetupIntent(
    config?: AxiosRequestConfig,
  ): Promise<SetupIntentResponse> {
    const orgID = await this.client.getOrgID();
    const response = await this.client.axios.post<SetupIntentResponse>(
      route(ENDPOINTS.SETUP_INTENT, {
        organization_id: orgID,
      }),
      {},
      config,
    );
    return response.data;
  }

  async listCreditUsagePerUser(
    config?: AxiosRequestConfig,
  ): Promise<ListCreditUsagePerUserResponse> {
    const orgID = await this.client.getOrgID();
    const response =
      await this.client.axios.get<ListCreditUsagePerUserResponse>(
        route(ENDPOINTS.LIST_CREDIT_USAGE_PER_USER, {
          organization_id: orgID,
        }),
        config,
      );
    return response.data;
  }

  async listBillingPlans(
    config?: AxiosRequestConfig,
  ): Promise<ListBillingPlansResponse> {
    const response = await this.client.axios.get<ListBillingPlansResponse>(
      ENDPOINTS.LIST_BILLING_PLANS,
      config,
    );
    return response.data;
  }

  async listPaymentMethods(
    config?: AxiosRequestConfig,
  ): Promise<ListPaymentMethodsResponse> {
    const orgID = await this.client.getOrgID();
    const response = await this.client.axios.get<ListPaymentMethodsResponse>(
      route(ENDPOINTS.LIST_PAYMENT_METHODS, {
        organization_id: orgID,
      }),
      config,
    );
    return response.data;
  }

  async updateSubscription(
    payload: Partial<UpdateSubscriptionPayload>,
    config?: AxiosRequestConfig,
  ): Promise<UpdateSubscriptionResponse> {
    const orgID = await this.client.getOrgID();
    const response = await this.client.axios.put<UpdateSubscriptionResponse>(
      route(ENDPOINTS.UPDATE_SUBSCRIPTION, {
        organization_id: orgID,
      }),
      payload,
      config,
    );
    return response.data;
  }

  async getSubscription(
    config?: AxiosRequestConfig,
  ): Promise<GetSubscriptionResponse> {
    const orgID = await this.client.getOrgID();
    const response = await this.client.axios.get<GetSubscriptionResponse>(
      route(ENDPOINTS.GET_SUBSCRIPTION, {
        organization_id: orgID,
      }),
      config,
    );
    return response.data;
  }

  async deleteStripePaymentMethod(
    paymentMethodId: string,
    config?: AxiosRequestConfig,
  ): Promise<void> {
    const orgID = await this.client.getOrgID();
    await this.client.axios.delete(
      route(ENDPOINTS.DELETE_PAYMENT_METHOD, {
        organization_id: orgID,
        payment_method_id: paymentMethodId,
      }),
      config,
    );
  }

  async listStripeInvoices(
    config?: AxiosRequestConfig,
  ): Promise<ListStripeInvoicesResponse> {
    const orgID = await this.client.getOrgID();
    const response = await this.client.axios.get<ListStripeInvoicesResponse>(
      route(ENDPOINTS.LIST_STRIPE_INVOICES, {
        organization_id: orgID,
      }),
      config,
    );
    return response.data;
  }

  async previewStripeInvoice(
    payload: PreviewStripeInvoicePayload,
    config?: AxiosRequestConfig,
  ): Promise<PreviewStripeInvoiceResponse> {
    const orgID = await this.client.getOrgID();
    const response = await this.client.axios.post<PreviewStripeInvoiceResponse>(
      route(ENDPOINTS.PREVIEW_STRIPE_INVOICE, {
        organization_id: orgID,
      }),
      payload,
      config,
    );
    return response.data;
  }
}
